import { ArrowRight, CalendarClock, Handshake, LineChart, MessagesSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MarketingFooter, MarketingHeader } from '@/modules/marketing/components/site-chrome';
import { INDUSTRIES } from '@/modules/marketing/components/industry-visuals';
import { usePageSeo } from '@/modules/marketing/hooks/use-page-seo';

const INDUSTRY_CARD_GRADIENTS = [
    'from-[#1e3a5f] to-[#2563eb]',
    'from-[#5b21b6] to-[#9333ea]',
    'from-[#0f766e] to-[#14b8a6]',
    'from-[#9d174d] to-[#ec4899]',
    'from-[#7c2d12] to-[#ea580c]',
] as const;

const VALUES = [
    {
        icon: Handshake,
        title: 'Guided, not guessing',
        description:
            'A conversation with a visitor should follow a plan your team actually trusts — not an unscripted model improvising on your behalf.',
    },
    {
        icon: LineChart,
        title: 'Decisions grounded in your own data',
        description:
            'Pricing and lead-scoring recommendations are only useful when they’re built from your numbers, not an industry-wide guess.',
    },
    {
        icon: MessagesSquare,
        title: 'One record per visitor',
        description:
            'A conversation, a lead, and a booked appointment are the same story told once — never three disconnected tools each holding a piece of it.',
    },
    {
        icon: CalendarClock,
        title: 'Fast to stand up',
        description: 'A new assistant should be live on a real website within a single setup session, branded and ready.',
    },
];

export function AboutPage() {
    usePageSeo({
        title: 'About AIPA — Our Mission & Team',
        description:
            "Learn about AIPA's mission to help businesses capture more leads and price smarter with AI — and meet the team building our AI Assistant, Pricing Intelligence, and CRM platform.",
        keywords: 'about AIPA, AIPA company, AI powered platform company, AIPA mission, AIPA team',
    });

    return (
        <div className="min-h-screen bg-background text-foreground">
            <MarketingHeader />

            <section className="mx-auto max-w-3xl px-6 py-20 text-center">
                <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">About AIPA</h1>
                <p className="mt-6 text-lg text-muted-foreground">
                    AIPA is a conversational lead-capture and pricing intelligence platform, built for teams tired of stitching together a
                    chat vendor, a CRM, a scheduling tool, and a spreadsheet to run their pipeline. We build one workspace instead, so a
                    visitor’s question, their contact details, and their booked appointment all live in the same place.
                </p>
            </section>

            <section className="border-t bg-muted/30 py-16">
                <div className="mx-auto max-w-6xl px-6">
                    <h2 className="text-center text-2xl font-semibold tracking-tight">What we believe</h2>
                    <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
                        {VALUES.map((value) => (
                            <div key={value.title} className="rounded-lg border bg-card p-6 shadow-sm">
                                <div className="flex size-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/70 text-primary-foreground">
                                    <value.icon className="size-6" />
                                </div>
                                <h3 className="mt-4 font-medium">{value.title}</h3>
                                <p className="mt-2 text-sm text-muted-foreground">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20">
                <div className="mx-auto max-w-6xl px-6">
                    <p className="text-center text-xs font-semibold uppercase tracking-wide text-primary">Who we build for</p>
                    <h2 className="mt-2 text-center text-2xl font-extrabold tracking-tight sm:text-3xl">
                        Purpose-built for ten industries — and counting
                    </h2>
                    <p className="mx-auto mt-3 max-w-xl text-center text-sm text-muted-foreground">
                        Every industry below ships with its own guided-question templates, tuned for how that business actually talks
                        to prospects.
                    </p>

                    <div className="mt-10 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
                        {INDUSTRIES.map((industry, i) => (
                            <Link
                                key={industry.slug}
                                to={`/industries/${industry.slug}`}
                                className="group overflow-hidden rounded-2xl border bg-card shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
                            >
                                <div
                                    className={`relative flex h-28 items-center justify-center overflow-hidden bg-gradient-to-br ${INDUSTRY_CARD_GRADIENTS[i % INDUSTRY_CARD_GRADIENTS.length]}`}
                                >
                                    <div className="absolute -right-6 -top-6 size-24 rounded-full bg-white/10 blur-2xl" />
                                    <div className="absolute -bottom-8 -left-6 size-24 rounded-full bg-white/10 blur-2xl" />
                                    <div className="relative flex size-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur transition-transform group-hover:scale-110">
                                        <industry.icon className="size-7 text-white" />
                                    </div>
                                </div>
                                <div className="p-3.5">
                                    <p className="text-sm font-bold tracking-tight">{industry.name}</p>
                                    <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground">{industry.tagline}</p>
                                    <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
                                        Learn more <ArrowRight className="size-3" />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <MarketingFooter />
        </div>
    );
}
